import {
  GAME_WIDGET_GROUPS,
  GAME_WIDGET_LIMIT,
  MINUTE,
  type GameRelevantListInput,
  type GameRelevantListSuccess,
  type RelevantGameData,
} from '@terros/common'
import { useTantalimQuery } from '../query'
import { gameRelevantListKey } from '../keys'
import { useTantalimInfiniteQuery, type GetInfiniteHookResponse } from '../infiniteQuery'
import { type GetHookResponse } from '../hooks'
import { toNewGameData } from './cache'

const COMPETITION_WIDGET_INPUT: GameRelevantListInput = { groups: GAME_WIDGET_GROUPS, limit: GAME_WIDGET_LIMIT }

function toNewRelevantGame(item: RelevantGameData): RelevantGameData {
  return { ...item, game: toNewGameData(item.game) }
}

/**
 * What a dashboard's competition widget shows: one page of the viewer's competitions, current first, then recently
 * ended, then those starting soon. `canCreate` comes with it so the widget knows whether to offer a way to add one.
 */
export function useCompetitionWidgetGames(enabled = true): GetHookResponse<GameRelevantListSuccess> {
  return useTantalimQuery<GameRelevantListInput, GameRelevantListSuccess, GameRelevantListSuccess>({
    url: '/game/relevant/list',
    queryKey: gameRelevantListKey(COMPETITION_WIDGET_INPUT),
    input: COMPETITION_WIDGET_INPUT,
    convertResponse: (data) => ({ ...data, games: data.games.map(toNewRelevantGame) }),
    options: { enabled, staleTime: 5 * MINUTE },
  })
}

/** Every competition the viewer can see, in the same order as the widget but unfiltered and a page at a time. */
export function useInfiniteRelevantGames(input: GameRelevantListInput = {}): GetInfiniteHookResponse<RelevantGameData> {
  return useTantalimInfiniteQuery<GameRelevantListInput, GameRelevantListSuccess, RelevantGameData>({
    url: '/game/relevant/list',
    queryKey: gameRelevantListKey(input),
    input,
    selectRecords: (page) => page.games.map(toNewRelevantGame),
    buildNextInput: (previous, cursor) => ({
      ...previous,
      sortTimestamp: typeof cursor === 'number' ? cursor : undefined,
    }),
  })
}
